/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { BarChart2, ChevronDown, ChevronRight, TableIcon } from 'lucide-react';
import { useState } from 'react';
import { Cell, Pie, PieChart } from 'recharts';

import { useGetStudentDistribution } from '~entities/studios/api/get-student-distribution';
import type {
  ClassDistribution,
  StudentDistribution,
} from '~entities/studios/api/get-student-distribution';

import { DataTableColumnHeader } from '~shared/components/table/header';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { ChartContainer, ChartTooltip } from '~shared/shadcn/ui/chart';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~shared/shadcn/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

const COLORS = [
  '#0088FE',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#8884d8',
  '#82ca9d',
];

interface ChartDataItem {
  name: string;
  value: number;
}

interface StudentDistributionPieChartProps {
  chartData: Array<ChartDataItem>;
}

function StudentDistributionPieChart({
  chartData,
}: StudentDistributionPieChartProps) {
  return (
    <ChartContainer
      config={{
        value: {
          label: 'Number of Students',
          color: 'hsl(var(--chart-5))',
        },
      }}
      className='h-[300px] w-full overflow-hidden sm:h-[500px]'
    >
      <PieChart width={800} height={500}>
        <Pie
          data={chartData}
          cx='50%'
          cy='50%'
          labelLine={false}
          label={({ name, percent }) =>
            `${name}: ${(percent * 100).toFixed(0)}%`
          }
          outerRadius={120}
          fill='#8884d8'
          dataKey='value'
        >
          {chartData.map((_, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <ChartTooltip formatter={(value: number) => [`${value} students`]} />
      </PieChart>
    </ChartContainer>
  );
}

const columnHelper = createColumnHelper<StudentDistribution>();

const classColumnHelper = createColumnHelper<ClassDistribution>();

const classColumns = [
  classColumnHelper.accessor('className', {
    header: 'Class Name',
    cell: (info) => {
      const value = info.getValue();
      const classId = info.row.original.classId;
      return (
        <Button variant='link' asChild>
          <Link to='/class-analytics/$classId' params={{ classId }}>
            {value}
          </Link>
        </Button>
      );
    },
  }),
  classColumnHelper.accessor('studentCount', {
    header: 'Students',
  }),
];

const NestedClassesTable = ({
  classes,
}: {
  classes: Array<ClassDistribution>;
}) => {
  const table = useReactTable({
    data: classes,
    columns: classColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Table>
      <TableHeader className='sticky top-0 bg-muted/50'>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id}>
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <TableCell
                key={cell.id}
                className={
                  cell.column.id === 'className'
                    ? 'min-w-[200px]'
                    : 'min-w-[100px]'
                }
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const StudentDistributionTable = ({
  data,
  groupBy,
}: {
  data: Array<StudentDistribution>;
  groupBy: 'className' | 'groupName';
}) => {
  const cols = [];
  if (groupBy === 'groupName') {
    cols.push(
      columnHelper.display({
        id: 'expander',
        cell: ({ row }) =>
          row.getCanExpand() ? (
            <Button
              variant='ghost'
              onClick={row.getToggleExpandedHandler()}
              className='p-0 hover:bg-transparent'
            >
              {row.getIsExpanded() ? (
                <ChevronDown className='size-4' />
              ) : (
                <ChevronRight className='size-4' />
              )}
            </Button>
          ) : null,
        enableHiding: true,
      }),
    );
  }
  cols.push(
    columnHelper.accessor('name', {
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title='Name' />
      ),
      cell: (info) => {
        const row = info.row.original;
        if (groupBy === 'className') {
          const classId = row.classes?.[0]?.classId;
          if (classId) {
            return (
              <Button variant='link' asChild>
                <Link to='/class-analytics/$classId' params={{ classId }}>
                  {info.getValue()}
                </Link>
              </Button>
            );
          }
        }
        return info.getValue();
      },
    }),
  );
  cols.push(
    columnHelper.accessor('studentCount', {
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title='Students' />
      ),
    }),
  );
  cols.push(
    columnHelper.accessor('studentPercent', {
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title='Percentage' />
      ),
      cell: (info) => {
        const value = info.getValue();
        return (
          <div>
            {typeof value === 'number' ? (value * 100).toFixed(1) : '-'}%
          </div>
        );
      },
    }),
  );

  const table = useReactTable<StudentDistribution>({
    data,
    columns: cols,
    getCoreRowModel: getCoreRowModel(),
    ...(groupBy === 'groupName' && {
      getExpandedRowModel: getExpandedRowModel(),
      getRowCanExpand: (row) =>
        row.original.classes && row.original.classes.length > 0,
      state: {
        columnVisibility: {
          expander: true,
        },
      },
    }),
  });

  return (
    <div className='rounded-md border'>
      <div className='max-h-[500px] overflow-auto'>
        <Table>
          <TableHeader className='sticky top-0 bg-background'>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) =>
              groupBy === 'groupName' ? (
                <>
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className='min-w-[100px]'>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {row.getIsExpanded() && (
                    <TableRow>
                      <TableCell colSpan={cols.length} className='p-0'>
                        <div className='bg-muted/50 px-4 py-2'>
                          <div className='max-h-[300px] overflow-auto'>
                            <NestedClassesTable
                              classes={row.original.classes}
                            />
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ) : (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className='min-w-[100px]'>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

interface StudentDistributionChartProps {
  sessionId: string;
}

export function StudentDistributionChart({
  sessionId,
}: StudentDistributionChartProps) {
  const [showTable, setShowTable] = useState(false);
  const [groupBy, setGroupBy] = useState<'className' | 'groupName'>(
    'className',
  );

  const { data: distributionData, isLoading } = useGetStudentDistribution({
    sessionId,
    groupBy,
  });

  const chartData =
    distributionData?.result?.map((item) => ({
      name: item.name,
      value: item.studentCount,
    })) ?? [];

  return (
    <Card className='col-span-1 flex h-full flex-col'>
      <CardHeader className='flex flex-col space-y-2 sm:flex-row sm:items-start sm:justify-between sm:space-y-0'>
        <div>
          <CardTitle>Student Distribution</CardTitle>
          <CardDescription>Enrollment numbers per class</CardDescription>
        </div>
        <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
          <Select
            value={groupBy}
            onValueChange={(value: 'className' | 'groupName') => {
              setGroupBy(value);
            }}
          >
            <SelectTrigger className='w-full sm:w-[180px]'>
              <SelectValue placeholder='Select grouping' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='className'>By Class Name</SelectItem>
              <SelectItem value='groupName'>By Group Name</SelectItem>
            </SelectContent>
          </Select>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='outline'
                  size='icon'
                  onClick={() => {
                    setShowTable(!showTable);
                  }}
                >
                  {showTable ? (
                    <BarChart2 className='size-4' />
                  ) : (
                    <TableIcon className='size-4' />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Switch to {showTable ? 'chart' : 'table'} view</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent className='flex-1 overflow-hidden'>
        {isLoading ? (
          <div className='flex h-[250px] items-center justify-center'>
            Loading...
          </div>
        ) : !showTable ? (
          <StudentDistributionPieChart chartData={chartData} />
        ) : (
          <div className='max-h-[calc(100vh-400px)] overflow-auto'>
            <StudentDistributionTable
              data={distributionData?.result ?? []}
              groupBy={groupBy}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
